import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IndiaMap } from "../components/IndiaMap";
import { Eyebrow, MaskLines } from "../components/Reveal";
import { scrollToId } from "../lib/scroll";

const Corner = ({ pos }) => {
  const map = {
    tl: "left-0 top-0 border-l border-t",
    tr: "right-0 top-0 border-r border-t",
    bl: "left-0 bottom-0 border-l border-b",
    br: "right-0 bottom-0 border-r border-b",
  };
  return <span className={`absolute h-4 w-4 border-gold ${map[pos]}`} aria-hidden="true" />;
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const mapY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const mapFade = useTransform(scrollYProgress, [0, 0.9], [1, 0.25]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-[76px]" data-testid="hero-section">
      {/* backdrop */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(1100px 600px at 82% 30%, rgba(201,162,75,0.07), transparent 60%), radial-gradient(800px 500px at 5% 85%, rgba(20,35,63,0.65), transparent 65%)",
        }}
      />

      <div className="wrap relative grid items-center gap-16 pb-24 pt-16 md:pt-24 lg:grid-cols-12 lg:gap-10 lg:pb-32 lg:pt-20">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <Eyebrow>ENEXUR India Market Intelligence™</Eyebrow>
          </motion.div>

          <h1 className="mt-10 font-display text-[2.35rem] leading-[1.08] tracking-[-0.015em] text-ivory sm:text-5xl lg:text-[3.4rem]">
            <MaskLines
              onMount
              delay={0.25}
              step={0.16}
              lines={[
                <span key="a">India is not the question.</span>,
                <em key="b" className="font-light text-gold-soft">
                  How should you enter India —
                </em>,
                <em key="c" className="font-light text-gold-soft">
                  and where should you start?
                </em>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-9 max-w-xl text-[17px] leading-relaxed text-mist"
          >
            For international companies evaluating, entering, or scaling in India.
            Build the evidence base for an India entry decision before committing
            capital, selecting a route to market, or deciding where to begin.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="mt-8 font-mono text-[11px] uppercase tracking-[0.34em] text-gold"
          >
            Private • Bespoke • Evidence-led
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-11 flex flex-col gap-4 sm:flex-row"
          >
            <Link
              to="/client-intelligence-brief"
              className="btn-gold"
              data-testid="hero-primary-cta"
            >
              Start Your Client Intelligence Brief
            </Link>
            <a
              href="#system"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("system");
              }}
              className="btn-ghost"
              data-testid="hero-secondary-cta"
            >
              Explore the Intelligence System
              <ArrowDown size={14} aria-hidden="true" />
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="mt-14 font-mono text-[10px] uppercase tracking-[0.3em] text-mist/50"
          >
            Executive dossier — India entry system
          </motion.p>
        </div>

        <motion.div
          style={{ y: mapY, opacity: mapFade }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative lg:col-span-5"
          data-testid="hero-map-panel"
        >
          <div className="relative border border-gold/20 bg-abyss/50 p-5 md:p-7">
            <Corner pos="tl" />
            <Corner pos="tr" />
            <Corner pos="bl" />
            <Corner pos="br" />
            <IndiaMap className="h-auto w-full" />
            <div className="mt-5 flex items-center justify-between border-t border-gold/15 pt-4 font-mono text-[9px] uppercase tracking-[0.22em] text-mist/70">
              <span>Fig. 01 — India / operational theatre</span>
              <span>68–97° E • 08–37° N</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
